import { pick, trim } from 'lodash';

import type { GenerationRequest } from 'src/data/generation';
import {
  GENERATION_TOOL,
  type GenerationTool,
  getToolBriefType,
  isBasicContentImproverTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';

import { isExtractingBriefType } from './briefType';
import { BRIEF_TYPE, type BriefType } from './const';
import type { Brief, BriefContent, BriefInput, EditingBriefContent } from './types';

export const UNIFIED_FIELD = {
  title: 'title',
  article: 'article',
} as const;

export const EMAIL_FIELD = {
  body: 'body',
} as const;

export const BRIEF_FORM_FIELD_NAMES = {
  ...UNIFIED_FIELD,
  url: 'url',
  contentTaxonomy: 'contentTaxonomy',
} as const;

export const SOCIAL_MANUAL_FIELD = {
  body: 'body',
} as const;

export const CANVAS_BRIEF_FIELD = {
  freeText: 'freeText',
} as const;

export const CONTEXT_BRIEF_FIELD = {
  general: 'general',
  textBefore: 'text_before',
  textAfter: 'text_after',
} as const;

export const defaultBriefContent = () => ({});

export const mapContentForEditing = (brief: Brief): EditingBriefContent | undefined => {
  if (isExtractingBriefType(brief.type)) {
    return {
      url: brief.url,
      title: brief.computedTitle || '',
      article: brief.computedArticle || '',
    };
  }

  switch (brief.type) {
    case BRIEF_TYPE.socialManual:
      return { [SOCIAL_MANUAL_FIELD.body]: brief.description };

    case BRIEF_TYPE.email:
      return pick(brief, Object.values(EMAIL_FIELD));

    case BRIEF_TYPE.canvas:
      return pick(brief, Object.values(CANVAS_BRIEF_FIELD));

    default:
      return undefined;
  }
};

export const mapExtractedContentForEditing = (brief: BriefContent): EditingBriefContent => {
  const { acTitleAiProvided = '', acArticleAiProvided = '', url } = brief;
  return {
    title: acTitleAiProvided,
    article: acArticleAiProvided,
    url,
  };
};

/**
 * Map any of currently used brief types to "BriefInputType", which is used in "document" generation mutations
 */
export const mapContentForBriefInput = (
  briefType: BriefType,
  briefContent: BriefContent
): BriefInput | undefined => {
  if (!briefContent) {
    return undefined;
  }

  switch (briefType) {
    case BRIEF_TYPE.unifiedContent:
    case BRIEF_TYPE.unifiedProduct:
      return {
        title: briefContent.title,
        body: briefContent.article,
        freeText: briefContent.body,
      };

    case BRIEF_TYPE.unifiedPromotionalEmail:
      return {
        title: briefContent.title,
        body: briefContent.article,
        freeText: briefContent.emailPromotion || briefContent.body,
      };

    case BRIEF_TYPE.email:
    case BRIEF_TYPE.socialManual:
      return {
        freeText: briefContent.body,
      };

    case BRIEF_TYPE.shopifyProductBrief:
      return {
        title: briefContent.productName,
        body: briefContent.body,
      };

    case BRIEF_TYPE.canvas: {
      return {
        freeText: briefContent.body || briefContent.freeText,
      };
    }

    default:
      return undefined;
  }
};

export const mapToolContentForBriefInput = (
  generationTool: GenerationTool,
  briefContent: BriefContent
): BriefInput | undefined => {
  const briefType = getToolBriefType(generationTool);
  if (!briefType) {
    return undefined;
  }

  return mapContentForBriefInput(briefType, briefContent);
};

export const getRecomposeTextFromBrief = (
  generationTool: GenerationTool,
  briefContent: BriefContent
): string => {
  if (!briefContent) {
    return '';
  }

  const briefType = getToolBriefType(generationTool);
  switch (briefType) {
    case BRIEF_TYPE.unifiedContent:
    case BRIEF_TYPE.unifiedProduct:
    case BRIEF_TYPE.unifiedPromotionalEmail:
      return briefContent.article || '';

    case BRIEF_TYPE.email:
    case BRIEF_TYPE.socialManual:
    case BRIEF_TYPE.shopifyProductBrief:
      return briefContent.body || '';

    case BRIEF_TYPE.canvas:
      return briefContent.freeText || '';

    default:
      return '';
  }
};

export const mapBriefInputToContent = (
  briefType: BriefType,
  briefInput: BriefInput
): BriefContent | undefined => {
  if (!briefInput) {
    return undefined;
  }

  switch (briefType) {
    case BRIEF_TYPE.unifiedContent:
    case BRIEF_TYPE.unifiedProduct:
      return {
        title: briefInput.title,
        article: briefInput.body,
      };

    case BRIEF_TYPE.unifiedPromotionalEmail:
      return {
        title: briefInput.title,
        article: briefInput.body,
        emailPromotion: briefInput.freeText,
      };

    case BRIEF_TYPE.email:
    case BRIEF_TYPE.socialManual:
      return {
        body: briefInput.freeText,
      };

    case BRIEF_TYPE.shopifyProductBrief:
      return {
        productName: briefInput.title,
        body: briefInput.body,
      };

    case BRIEF_TYPE.canvas: {
      return {
        freeText: briefInput.freeText,
      };
    }

    default:
      return undefined;
  }
};

export const mapBriefFromGenerationResult = (
  generationTool: GenerationTool,
  request: GenerationRequest
): BriefContent | undefined => {
  if (isRewriteGenerationTool(generationTool) || isBasicContentImproverTool(generationTool)) {
    return undefined;
  }

  const briefType = getToolBriefType(generationTool);
  const { briefTitle, briefBody, emailPromotion } = request;

  switch (briefType) {
    case BRIEF_TYPE.unifiedContent:
    case BRIEF_TYPE.unifiedProduct:
      return {
        title: briefTitle,
        article: briefBody,
      };

    case BRIEF_TYPE.unifiedPromotionalEmail:
      return {
        title: briefTitle,
        article: briefBody,
        emailPromotion,
      };

    case BRIEF_TYPE.email:
    case BRIEF_TYPE.socialManual:
      return {
        body: briefBody,
      };

    case BRIEF_TYPE.shopifyProductBrief:
      return {
        productName: briefTitle,
        body: briefBody,
      };

    case BRIEF_TYPE.canvas: {
      return {
        freeText: briefBody,
      };
    }

    default:
      return undefined;
  }
};

export const shouldHaveUniqueCacheValue = (generationTool: GenerationTool): boolean => {
  const list: GenerationTool[] = [
    GENERATION_TOOL.basicPersonalBio,
    GENERATION_TOOL.basicCompanyBio,
    GENERATION_TOOL.basicEngagingQuestions,
    GENERATION_TOOL.basicQuoraAnswers,
  ];
  return list.includes(generationTool);
};

export const generateBriefCacheKey = (generationTool: GenerationTool): string => {
  const briefType = getToolBriefType(generationTool);
  if (shouldHaveUniqueCacheValue(generationTool)) {
    return `tool_${generationTool}`;
  }
  return `brief_type_${briefType}`;
};

export const checkBriefHasContent = (briefType: BriefType, briefContent: BriefContent): boolean => {
  const data = mapContentForBriefInput(briefType, briefContent);
  if (!data) {
    return false;
  }

  return trim(data.title) !== '' || trim(data.body) !== '' || trim(data.freeText) !== '';
};

export const isEnoughContentForExtraction = (content: string): boolean => {
  if (!content) {
    return false;
  }

  // over 5 words, continue generation
  return content.split(' ').length <= 5;
};
